import { lastStep } from './stepper.machine';
import * as stepperAction from './stepper.slice';
import type {
  EventType,
  StepListener,
  StepState,
  StepperListener,
} from './stepper.types';
import { getStepState, isADesignStep } from './stepper.utils';

import { StepName } from '~/core/enums';
import { type AppStartListening } from '~/redux/listenerMiddleware';
import * as colorAction from '~/redux/slices/color/color.slice';
import * as materialAction from '~/redux/slices/material/material.slice';
import * as shapeAction from '~/redux/slices/shape/shape.slice';
import * as storeAction from '~/redux/slices/store/store.slice';
import * as visitedStepsAction from '~/redux/slices/visitedSteps/visitedSteps.slice';

const listenPrevRequiredStep: StepListener = {
  event: {
    onUpdate: [visitedStepsAction.updateVisitedSteps.type],
    onReset: [],
  },
  isStepEnabled: (state) =>
    state.stepper.steps.includes(StepName.designCornerRadius)
      ? state.visitedSteps.visitedSteps.includes(StepName.designCornerRadius)
      : state.visitedSteps.visitedSteps.includes(StepName.designEdge),
  predicate: (prevState, currState) => {
    const [prev, curr] = [prevState, currState].map((state) =>
      state.stepper.steps.includes(StepName.designCornerRadius)
        ? state.visitedSteps.visitedSteps.includes(StepName.designCornerRadius)
        : state.visitedSteps.visitedSteps.includes(StepName.designEdge)
    );

    return prev !== curr;
  },
};

export const stepperListener: StepperListener = {
  [StepName.store]: {
    event: { onUpdate: [], onReset: [] },
    isStepEnabled: () => true,
    predicate: () => false,
  },
  [StepName.material]: {
    event: {
      onUpdate: [storeAction.updateSelectedStore.type],
      onReset: [],
    },
    isStepEnabled: (state) => !!state.store.selectedStore?.companyId,
    predicate: (prevState, currState) =>
      prevState.store.selectedStore?.companyId !==
      currState.store.selectedStore?.companyId,
  },
  [StepName.color]: {
    event: {
      onUpdate: [materialAction.updateSelectedMaterial.type],
      onReset: [materialAction.resetSelectedMaterial.type],
    },
    isStepEnabled: (state) => !!state.material.selectedMaterial?.id,
    predicate: (prevState, currState) => {
      const materialIdChanged =
        prevState.material.selectedMaterial?.id !==
        currState.material.selectedMaterial?.id;

      const materialSelectedButColorNotEnabled =
        !!currState.material.selectedMaterial?.id &&
        !currState.stepper.enabledSteps.includes(StepName.color);

      return materialIdChanged || materialSelectedButColorNotEnabled;
    },
  },
  [StepName.designShape]: {
    event: {
      onUpdate: [colorAction.updateSelectedColorId.type],
      onReset: [colorAction.resetSelectedColorId.type],
    },
    isStepEnabled: (state) => !!state.color.selectedColorId,
    predicate: (prevState, currState) =>
      prevState.color.selectedColorId !== currState.color.selectedColorId,
  },
  [StepName.designEdge]: {
    event: {
      onUpdate: [shapeAction.updateSelectedDrawingObjectName.type],
      onReset: [shapeAction.resetSelectedPredefinedShape.type],
    },
    isStepEnabled: (state) => state.shape.selectedDrawingObjectName?.length > 0,
    predicate: (prevState, currState) =>
      prevState.shape.selectedDrawingObjectName !==
      currState.shape.selectedDrawingObjectName,
    effect: (eventType, api) => {
      if (eventType === 'onReset') {
        api.dispatch(visitedStepsAction.resetVisitedSteps(StepName.designEdge));
        api.dispatch(stepperAction.goToStep(StepName.designShape));
      }
    },
  },
  [StepName.designCornerRadius]: {
    event: {
      onUpdate: [visitedStepsAction.updateVisitedSteps.type],
      onReset: [],
    },
    isStepEnabled: (state) =>
      !state.stepper.steps.includes(StepName.designCornerRadius) ||
      state.visitedSteps.visitedSteps.includes(StepName.designEdge),
    predicate: (prevState, currState) => {
      const [prev, curr] = [prevState, currState].map(
        (state) =>
          !state.stepper.steps.includes(StepName.designCornerRadius) ||
          state.visitedSteps.visitedSteps.includes(StepName.designEdge)
      );

      return prev !== curr;
    },
  },
  [StepName.designDiagonalCorner]: listenPrevRequiredStep,
  [StepName.designCustomSide]: listenPrevRequiredStep,
  [StepName.designSink]: listenPrevRequiredStep,
  [StepName.designCookingAppliance]: listenPrevRequiredStep,
  [StepName.designCutout]: listenPrevRequiredStep,
  [StepName.designTearOut]: listenPrevRequiredStep,
  [StepName.designAddOn]: listenPrevRequiredStep,
  [StepName.designPlumbing]: listenPrevRequiredStep,
  [lastStep]: listenPrevRequiredStep,
  [StepName.thankYou]: {
    event: { onUpdate: [], onReset: [] },
    isStepEnabled: () => true,
    predicate: () => false,
  },
};

export const stepperListeners: [StepState, StepListener][] = Object.entries(
  stepperListener
).map(([stepName, stepListener]) => [
  getStepState(stepName as StepName),
  stepListener,
]);

export const onResetEventsListener = (startListening: AppStartListening) =>
  startListening({
    predicate: (action, currState, prevState) =>
      stepperListeners.some(
        ([_, listener]) =>
          action.payload === undefined &&
          listener.event.onReset.includes(action.type) &&
          listener.predicate(prevState, currState)
      ),

    effect: (action, api) =>
      filterListeners('onReset', action.type).forEach(([step, listener]) => {
        listener.effect?.('onReset', api, action);
        api.dispatch(stepperAction.resetStepper(step.current));
      }),
  });

export const onUpdateEventsListener = (startListening: AppStartListening) =>
  startListening({
    predicate: (action, currState, prevState) =>
      stepperListeners.some(
        ([_, listener]) =>
          listener.event.onUpdate.includes(action.type) &&
          listener.predicate(prevState, currState) &&
          action.payload !== undefined
      ),

    effect: (action, api) =>
      filterListeners('onUpdate', action.type)
        .filter(([step, listener]) => {
          const state = api.getState();

          return (
            !state.stepper.steps.includes(step.current) ||
            listener.isStepEnabled(state)
          );
        })
        .forEach(([step, listener]) => {
          listener.effect?.('onUpdate', api, action);
          const state = api.getState();
          const allowAutoNav = state.stepper.allowAutoNavigation;
          const isOnDesignSection = isADesignStep(step.current);
          const shouldReset = !listener.isStepEnabled(state);

          if (!isOnDesignSection && allowAutoNav && shouldReset) {
            api.dispatch(stepperAction.resetStepper(step.next));
          }
          api.dispatch(stepperAction.enableStep(step.current));
        }),
  });

export const filterListeners = (
  eventType: EventType,
  actionType: string
): [StepState, StepListener][] =>
  stepperListeners.filter(([_, listener]) =>
    listener.event[eventType].includes(actionType)
  );
